import React, { useState, useEffect } from "react"
import { DragDropProvider } from "@dnd-kit/react"
import { move } from "@dnd-kit/helpers"
import { useToast } from "~/utils/Toast"

import LoadingWrapper from "~/library/LoadingWrapper"
import DroppableStatusSection from "./DroppableStatusSection"
import NewTask from "./NewTask"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceTasks, apiWorkspaceAccounts, apiWorkspaceContacts, apiWorkspaceDeals } from "~/routes"
import type Task from "~/types/Task"
import { Plus } from "lucide-react"

interface TasksManagerProps {
    accountId?: string
    contactId?: string
    dealId?: string
}

const TasksManager: React.FC<TasksManagerProps> = ({ accountId, contactId, dealId }) => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()

    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
        not_started: true,
        in_progress: true,
        completed: true,
        cancelled: false,
    })

    const [tasks, setTasks] = useState<{ [key: string]: Task[] }>({})
    const [isLoading, setIsLoading] = useState(false)
    const [showNewTaskModal, setShowNewTaskModal] = useState(false)

    const [internalAccountId, setInternalAccountId] = useState<string>("")
    const [internalContactId, setInternalContactId] = useState<string>("")
    const [internalDealId, setInternalDealId] = useState<string>("")

    const statusKeys = ["not_started", "in_progress", "completed", "cancelled"]
    const statusLabels = {
        not_started: "Not Started",
        in_progress: "In Progress",
        completed: "Completed",
        cancelled: "Cancelled",
    }

    useEffect(() => {
        const resolveIds = async () => {
            if (accountId) {
                const endpoint = apiWorkspaceAccounts.show.path({ venue_slug: venueSlug, id: accountId })
                const response = await fetch(endpoint)
                const data = await response.json()
                setInternalAccountId(data.account.id)
            }

            if (contactId) {
                const endpoint = apiWorkspaceContacts.show.path({ venue_slug: venueSlug, id: contactId })
                const response = await fetch(endpoint)
                const data = await response.json()
                setInternalContactId(data.contact.id)
            }

            if (dealId) {
                const endpoint = apiWorkspaceDeals.show.path({ venue_slug: venueSlug, id: dealId })
                const response = await fetch(endpoint)
                const data = await response.json()
                setInternalDealId(data.deal.id)
            }
        }

        resolveIds()
    }, [accountId, contactId, dealId])

    useEffect(() => {
        if (
            (!accountId || internalAccountId) &&
            (!contactId || internalContactId) &&
            (!dealId || internalDealId)
        ) {
            fetchTasks()
        }
    }, [internalAccountId, internalContactId, internalDealId])

    const fetchTasks = async () => {
        setIsLoading(true)
        try {
            const endpoint = apiWorkspaceTasks.index.path({ venue_slug: venueSlug })

            const params = new URLSearchParams()
            if (internalAccountId) params.append("q[account_id_eq]", internalAccountId)
            if (internalContactId) params.append("q[contact_id_eq]", internalContactId)
            if (internalDealId) params.append("q[deal_id_eq]", internalDealId)

            const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint

            const response = await fetch(url, {
                headers: { 'Accept': 'application/json' }
            })

            const data = await response.json()
            normalizeTasks(data.tasks || [])
        } catch (error) {
            console.error("Error fetching tasks:", error)
        } finally {
            setIsLoading(false)
        }
    }

    const normalizeTasks = (tasksData: Task[]) => {
        const normalized = statusKeys.reduce((acc, status) => {
            acc[status] = tasksData.filter((task) => task.status === status)
            return acc
        }, {} as { [key: string]: Task[] })

        setTasks(normalized)
        prepareExpandedSections(normalized)
    }

    const prepareExpandedSections = (tasks: { [key: string]: Task[] }) => {
        setExpandedSections((prev) => ({
            ...prev,
            not_started: tasks.not_started.length > 0 || prev.not_started,
            in_progress: tasks.in_progress.length > 0 || prev.in_progress,
        }))
    }

    const toggleSection = (status: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [status]: !prev[status],
        }))
    }

    const handleDragStart = () => {}

    const handleDragOver = (event: any) => {
        const { source } = event.operation
        if (source?.type !== "task") return
        setTasks((currentTasks) => move(currentTasks, event))
    }

    const handleDragEnd = async (event: any) => {
        const { source, target } = event.operation

        if (!target) return

        if (event.canceled) {
            fetchTasks()
            return
        }

        const newStatus = target.id
        const taskId = source.id

        const task = Object.values(tasks)
            .flat()
            .find((t) => t.prefixId === taskId)

        if (!task || task.status === newStatus) {
            return
        }

        setTasks((currentTasks) => {
            const updatedTasks = { ...currentTasks }

            Object.keys(updatedTasks).forEach((status) => {
                updatedTasks[status] = updatedTasks[status].filter((t) => t.prefixId !== taskId)
            })

            if (updatedTasks[newStatus]) {
                updatedTasks[newStatus] = [...updatedTasks[newStatus], { ...task, status: newStatus }]
            }

            return updatedTasks
        })

        const url = apiWorkspaceTasks.update.path({
            venue_slug: venueSlug,
            id: taskId,
        })

        try {
            const response = await fetch(url, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    task: {
                        status: newStatus,
                    },
                }),
            })

            if (!response.ok) {
                toast("Error updating task status", "danger")
                fetchTasks()
            } else {
                toast("Task status updated", "success")
            }
        } catch (error) {
            toast("Error updating task status", "danger")
            fetchTasks()
        }
    }

    const deleteTask = async (taskPrefixId: string) => {
        const url = apiWorkspaceTasks.destroy.path({
            venue_slug: venueSlug,
            id: taskPrefixId,
        })

        try {
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            })

            if (response.ok) {
                setTasks((currentTasks) => {
                    const updatedTasks = { ...currentTasks }
                    Object.keys(updatedTasks).forEach((status) => {
                        updatedTasks[status] = updatedTasks[status].filter(
                            (task) => task.prefixId !== taskPrefixId
                        )
                    })
                    return updatedTasks
                })
                toast("Task deleted successfully", "success")
            } else {
                toast("Error deleting task", "danger")
            }
        } catch (error) {
            toast("Error deleting task", "danger")
        }
    }

    const handleTaskSuccess = () => {
        fetchTasks()
    }

    return (
        <div className="row">
            <div className="col-12">
                <LoadingWrapper isLoading={isLoading}>
                    <div className="d-flex justify-content-end mb-2">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowNewTaskModal(true)}
                        >
                            <Plus size={16} className="me-2" />
                            New Task
                        </button>
                    </div>

                    {Object.keys(tasks).length > 0 ? (
                        <DragDropProvider
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                        >
                            <div>
                                {statusKeys.map((status) => {
                                    const statusTasks = tasks[status] || []
                                    const statusLabel = statusLabels[status as keyof typeof statusLabels]

                                    return (
                                        <DroppableStatusSection
                                            key={status}
                                            status={status}
                                            statusLabel={statusLabel}
                                            tasks={statusTasks}
                                            isExpanded={expandedSections[status]}
                                            onToggle={() => toggleSection(status)}
                                            onDeleteTask={deleteTask}
                                            onTaskUpdated={handleTaskSuccess}
                                        />
                                    )
                                })}

                                {Object.values(tasks).every((statusTasks) => statusTasks.length === 0) && (
                                    <div className="text-center py-4">
                                        <p className="text-muted mb-0">No tasks have been created yet</p>
                                    </div>
                                )}
                            </div>
                        </DragDropProvider>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-muted mb-0">No tasks have been created yet</p>
                        </div>
                    )}
                </LoadingWrapper>

                {showNewTaskModal && (
                    <NewTask
                        isOpen={showNewTaskModal}
                        onClose={() => setShowNewTaskModal(false)}
                        onSuccess={handleTaskSuccess}
                        accountId={accountId}
                        contactId={contactId}
                        dealId={dealId}
                    />
                )}
            </div>
        </div>
    )
}

export default TasksManager
